import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import SearchParams from "simplydo/core/searchParams";
import type { Invitation, OpenAPI } from "simplydo/interfaces";
import { Divider } from "simplydo/react/ui/divider";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";
import { Message } from "simplydo/react/ui/message";

import api from "api";

import InvitationsView from "./InvitationsView";
import type { InvitationType, InvitationsByType } from "./types";

type TypeGroupDescription = {
  [key in InvitationType]: { name: string; description: string };
};

export const InvitationList = ({
  accepted = false,
  createdBySelf = false,
}: {
  accepted?: boolean;
  createdBySelf?: boolean;
}) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new SearchParams(location.search),
    [location.search],
  );

  const { t } = useTranslation();

  const invitationGroups: TypeGroupDescription = {
    group: {
      name: t("invitations.group.membership"),
      description: t("invitations.group.membershipInfo"),
    },
    requestJoinGroup: { name: "", description: "" },
    requestJoinIdea: { name: "", description: "" },
    idea: {
      name: `${t("common:capitalise", { key: "generic.idea" })} collaboration`,
      description: `Only members of an ${t("generic.idea")} team can modify its contents. You can manage invitations and requests to collaborate on the following ${t("generic.ideas")} to help them grow.`,
    },
    ideaBusinessProfile: {
      name: "Business profile membership",
      description: "You were invited to join the following business profiles.",
    },
    ideaAssessor: {
      name: `${t("common:capitalise", { key: "generic.idea" })} assessment`,
      description: `You were asked to assess ${t("generic.ideas")} in the following ${t("generic.challenges")}.`,
    },
    projectInvited: {
      name: "Project board assignment",
      description: `You were assigned to ${t("generic.ideas")} in the following projects:`,
    },
  };

  const filteredInvitations = useMemo<InvitationsByType>(() => {
    const invitationsByType = invitations.reduce<InvitationsByType>(
      (acc, invitation) => {
        let forType = invitation.forType;
        if (forType === "requestJoinGroup") {
          forType = "group";
        }
        if (forType === "requestJoinIdea") {
          forType = "idea";
        }
        if (createdBySelf !== invitation.createdByCurrentUser) {
          return acc;
        }
        if (!acc[forType]) {
          acc[forType] = [];
        }
        acc[forType]!.push(invitation);
        return acc;
      },
      {},
    );

    if (!search) {
      return invitationsByType;
    }

    const lowerSearch = search.toLowerCase();
    return Object.keys(invitationsByType).reduce<InvitationsByType>(
      (acc, forTypeKey) => {
        const forType = forTypeKey as InvitationType;
        if (!invitationsByType[forType]) {
          return acc;
        }
        const filtered = invitationsByType[forType]!.filter((invitation) => {
          if (
            invitation.forType === "group" ||
            invitation.forType === "requestJoinGroup"
          ) {
            if (invitation.group?.name?.toLowerCase().includes(lowerSearch)) {
              return true;
            }
          } else if (invitation.forType === "ideaBusinessProfile") {
            if (
              invitation.ideaBusinessProfile?.name
                ?.toLowerCase()
                ?.includes(lowerSearch)
            ) {
              return true;
            }
          } else if (
            invitation.forType === "idea" ||
            invitation.forType === "requestJoinIdea" ||
            invitation.forType === "projectInvited"
          ) {
            if (invitation.idea?.name?.toLowerCase().includes(lowerSearch)) {
              return true;
            }
          } else if (invitation.forType === "ideaAssessor") {
            if (
              invitation.challenge?.name?.toLowerCase().includes(lowerSearch)
            ) {
              return true;
            }
          }
          if (
            invitation.inviterUser?.profile?.fullName
              ?.toLowerCase()
              .includes(lowerSearch)
          ) {
            return true;
          }
          return false;
        });
        if (filtered.length > 0) {
          acc[forType] = filtered;
        }
        return acc;
      },
      {},
    );
  }, [search, invitations, createdBySelf]);

  const reloadInvitations = useCallback(
    (withLoader = true) => {
      if (withLoader) {
        setLoading(true);
      }
      const apiRequest = accepted
        ? api.invitations.getAccepted
        : api.invitations.get;
      apiRequest(
        (data) => {
          setInvitations(data.invitations);
          setLoading(false);
        },
        () => {
          setLoading(false);
        },
      );
    },
    [accepted],
  );

  useEffect(() => {
    reloadInvitations();
  }, [reloadInvitations]);

  useEffect(() => {
    const reload = params.get("reload");
    if (reload) {
      reloadInvitations(false);
      params.delete("reload");
      navigate({ search: params.toString() });
    }
  }, [params, navigate, reloadInvitations]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Input
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Divider hidden />
      {Object.keys(filteredInvitations).length === 0 ? (
        <Message>No invitations to show.</Message>
      ) : (
        (Object.keys(invitationGroups) as InvitationType[]).map((forType) =>
          filteredInvitations[forType] &&
          filteredInvitations[forType]!.length !== 0 ? (
            <div key={forType}>
              <H3>
                {invitationGroups[forType].name}
                <Subheader>{invitationGroups[forType].description}</Subheader>
              </H3>
              <InvitationsView
                invitations={filteredInvitations[forType]!}
                hideTitle
                showCreatedBySelf={createdBySelf}
              />
            </div>
          ) : null,
        )
      )}
    </>
  );
};
